import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Icon, Link, Text } from '@amzn/storm-ui';
import { LinkProps } from '@amzn/aac-chrome-internal-api';
import { externalLink, questionCircleOutline } from '@amzn/storm-ui-icons';
import { selectHelpMenu } from 'src/features/header/store/slice';
import { RootState } from 'src/app/store';
import { FocusModeUtilitiesIconButton, UtilitiesIconButton } from '../styles';
import { HelpDropdownPublisher } from 'src/commons/utils/metrics';
import { CustomPopper } from '../CustomPopper/CustomPopper';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import { useAccountDropdownContext } from '../AccountDropdown/AccountDropdownProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider/InternalChromeConfigContextProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { squidInk } from 'src/colors';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

// TODO: Remove aac-help-menu-item-feature-center usage after the back-end cleanup
// https://issues.amazon.com/issues/P209748810
const featureCenterUid = 'aac-help-menu-item-feature-center';
const supportCenterUid = 'aac-help-menu-item-support-center';
const adsStatusUid = 'aac-help-menu-item-ads-status';
const helpMenuDropdownId = 'aac-support-center-dropdown';

const StyledHelpMenuLink = styled(Link)`
  &&&& {
    display: block;
    padding: ${({ theme }) => theme.spacing.xsmall.default};
    text-decoration: none;
    color: ${squidInk};

    &:hover {
      background-color: rgba(111, 111, 111, 0.1);
      text-decoration: none;
      color: ${squidInk};
    }
  }
`;

const HelpMenuLink: React.VFC<{ helpLinks: LinkProps[] }> = ({ helpLinks }) => {
  const { hasAccessToSA, hasAccessToDSP } = useAccountDropdownContext();

  return (
    <>
      {helpLinks.map(({ label, url, isExternal, uid }) => (
        <StyledHelpMenuLink
          key={label}
          href={
            uid === supportCenterUid
              ? `${url}${
                  url.includes('?') ? '&' : '?'
                }hasAccessToSA=${hasAccessToSA}&hasAccessToDSP=${hasAccessToDSP}`
              : url
          }
          target="_blank"
          rel="noreferrer"
          {...getTaktAttributes(
            `help-dropdown-link${uid ? '-' + uid : ''}`,
            chromeComponents.HEADER,
            {
              url,
              ...(!!uid && { uid }),
            },
          )}
        >
          <Text>
            {label} {isExternal === true && <Icon type={externalLink} />}
          </Text>
        </StyledHelpMenuLink>
      ))}
    </>
  );
};

interface HelpDropdownProps {
  helpInfo: Array<LinkProps>;
}

const HelpDropdown: React.VFC<HelpDropdownProps> = ({ helpInfo }) => {
  const chromeConfig = useChromeConfigContext();
  const [isOpen, setIsOpen] = useState(false);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  const handleToggle = () => {
    if (isOpen) {
      HelpDropdownPublisher.publishCounterMonitor('Close', 1);
    } else {
      HelpDropdownPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
  };
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const StyledIconButton =
    chromeConfig.pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS
      ? FocusModeUtilitiesIconButton
      : UtilitiesIconButton;

  const isHMHIngressEnabled = useIsFeatureEnabled(
    WeblabedFeatures.HMH_IN_HEADER_QUESTION_MARK,
  );

  const helpLinks = helpInfo.filter((helpLink) => {
    if (internalChromeConfig.retailerId || isHMHIngressEnabled) {
      return helpLink.uid !== featureCenterUid && helpLink.uid !== adsStatusUid;
    }
    return helpLink.uid !== featureCenterUid;
  });

  return (
    <CustomPopper
      zIndex={500}
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <StyledIconButton
          onClick={handleToggle}
          aria-label={helpInfo?.[0]?.label}
          aria-haspopup={true}
          aria-expanded={isOpen}
          data-ccx-e2e-id={helpMenuDropdownId}
          size="small"
          {...getTaktAttributes(helpMenuDropdownId, chromeComponents.HEADER)}
        >
          <Icon type={questionCircleOutline} />
        </StyledIconButton>
      }
      position="bottom"
      align="center"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
    >
      <div data-ccx-e2e-id="support-center">
        <HelpMenuLink helpLinks={helpLinks} />
      </div>
    </CustomPopper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    helpInfo: selectHelpMenu(state),
  };
};

export default connect(mapStateToProps)(HelpDropdown);
